import React, { useState } from 'react';
import { User, Mail, Phone, Briefcase, Building2, Hash, ArrowLeft, Save, ToggleLeft, ToggleRight, Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2';
import config from "../config";

const Field = ({ icon, field, label, type = 'text', placeholder = '', required = false, value, onChange, error }) => (
  <div>
    <label>{label} {required && <span style={{ color: '#ef4444' }}>*</span>}</label>
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none', zIndex: 1 }}>
        {icon}
      </div>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        className={`input-field ${error ? 'input-error' : ''}`}
        style={{ paddingLeft: '2.75rem' }}
      />
    </div>
    {error && <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.3rem' }}>{error}</p>}
  </div>
);

const AddCustomerList = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullNames: '',       // maps to Users.FullNames
    email: '',
    phone: '',           // maps to Users.Phone
    address: '',         // maps to Users.Address
    about: '',           // maps to Users.About
    role: 'user',        // default role
    language: 'en',      // default language
    status: 'offline',   // default status
    isActive: 1,
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Correct endpoint: POST /api/Users/createuser — [FromBody]
  const API_URL = `${config.API_BASE_URL}/Users/createuser`;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    if (!formData.fullNames.trim()) newErrors.fullNames = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#f59e0b'
      });
      return;
    }

    setLoading(true);
    try {
      // Build payload matching the Users model expected by [FromBody]
      const payload = {
        Username: formData.username,
        Password: formData.password,
        FullNames: formData.fullNames,
        Email: formData.email,
        Phone: formData.phone || null,
        Address: formData.address || null,
        About: formData.about || null,
        Role: formData.role,
        Language: formData.language,
        Status: formData.status,
        IdentificationCode: crypto.randomUUID(), // generate on frontend as fallback
        RegistrationDate: new Date().toISOString(),
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'User Created!',
          text: `${formData.fullNames} has been added successfully.`,
          background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#10b981'
        });
        resetForm();
      } else {
        const errText = await response.text();
        Swal.fire({
          icon: 'error',
          title: `Error ${response.status}`,
          text: errText || 'Failed to create user.',
          background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#ef4444'
        });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      Swal.fire({
        icon: 'error', title: 'Error',
        text: 'Failed to create user. Please try again.',
        background: '#1e293b', color: '#f1f5f9', confirmButtonColor: '#ef4444'
      });
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      username: '', password: '', fullNames: '', email: '',
      phone: '', address: '', about: '',
      role: 'user', language: 'en', status: 'offline', isActive: 1,
    });
    setErrors({});
  };



  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: '2rem', fontFamily: '"Inter", -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        label { display: block; margin-bottom: 0.4rem; color: #94a3b8; font-weight: 500; font-size: 0.85rem; }
        .input-field { width: 100%; padding: 0.75rem 0.875rem; background: #0f172a; border: 1px solid #334155; border-radius: 10px; color: #f1f5f9; font-size: 0.9rem; transition: all 0.3s ease; font-family: 'Inter', sans-serif; box-sizing: border-box; }
        .input-field:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
        .input-field::placeholder { color: #475569; }
        .input-field:disabled { opacity: 0.5; cursor: not-allowed; }
        .input-error { border-color: #ef4444 !important; box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important; }
        .select-field { width: 100%; padding: 0.75rem 2.5rem 0.75rem 2.75rem; background: #0f172a; border: 1px solid #334155; border-radius: 10px; color: #f1f5f9; font-size: 0.9rem; transition: all 0.3s ease; font-family: 'Inter', sans-serif; box-sizing: border-box; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.875rem center; cursor: pointer; }
        .select-field:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
        .select-field option { background: #1e293b; }
        .form-section { background: linear-gradient(135deg, #1e293b 0%, #162032 100%); border: 1px solid #334155; border-radius: 16px; padding: 2rem; margin-bottom: 1.5rem; position: relative; overflow: hidden; }
        .form-section::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, #10b981, #3b82f6); }
        .section-title { color: #f1f5f9; font-size: 1.05rem; font-weight: 600; margin-bottom: 1.5rem; display: flex; align-items: center; gap: 0.5rem; padding-bottom: 0.75rem; border-bottom: 1px solid #334155; }
        .btn { padding: 0.875rem 2rem; border-radius: 10px; border: none; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: inline-flex; align-items: center; gap: 0.5rem; font-family: 'Inter', sans-serif; font-size: 0.95rem; }
        .btn-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
        .btn-success:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(16,185,129,0.4); }
        .btn-success:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-secondary { background: #334155; color: #f1f5f9; }
        .btn-secondary:hover { background: #475569; transform: translateY(-1px); }
        .btn-outline { background: transparent; color: #64748b; border: 1px solid #334155; }
        .btn-outline:hover { background: #1e293b; color: #f1f5f9; border-color: #475569; }
        .spinner-sm { border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; width: 18px; height: 18px; animation: spin 0.8s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .preview-card { background: #0f172a; border: 1px solid #334155; border-radius: 12px; padding: 1.5rem; display: flex; align-items: center; gap: 1rem; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #1e293b; }
        ::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
      `}</style>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <a href="/customers" style={{ textDecoration: 'none' }}>
            <button className="btn btn-outline" style={{ padding: '0.625rem 1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
          </a>
          <div>
            <h1 style={{ fontSize: '2.2rem', fontWeight: '700', background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>
              Add New Customer
            </h1>
            <p style={{ color: '#64748b', fontSize: '1rem' }}>Fill in the details to register a new user</p>
          </div>
        </div>

        {/* Live Preview */}
        {(formData.fullNames || formData.username) && (
          <div className="form-section" style={{ marginBottom: '1.5rem' }}>
            <div className="section-title"><User size={18} color="#10b981" />Preview</div>
            <div className="preview-card">
              <div style={{ width: '60px', height: '60px', borderRadius: '12px', background: 'linear-gradient(135deg, #10b981, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', fontWeight: '700', color: 'white', flexShrink: 0 }}>
                {formData.fullNames?.[0]?.toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#f1f5f9', fontWeight: '600', fontSize: '1.05rem' }}>{formData.fullNames || '—'}</p>
                <p style={{ color: '#64748b', fontSize: '0.85rem' }}>{formData.username ? `@${formData.username}` : 'No username yet'}</p>
                {formData.email && <p style={{ color: '#60a5fa', fontSize: '0.8rem', marginTop: '0.2rem' }}>{formData.email}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '500', background: formData.isActive === 1 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: formData.isActive === 1 ? '#34d399' : '#f87171', border: `1px solid ${formData.isActive === 1 ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                  {formData.isActive === 1 ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        )}

        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1.5rem' }}>
          <span style={{ color: '#ef4444' }}>*</span> Required fields
        </p>

        {/* Personal Info */}
        <div className="form-section">
          <div className="section-title"><User size={18} color="#10b981" />Personal Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }}>
            <Field icon={<User size={16} />} field="fullNames" label="Full Name" placeholder="e.g. Juan Dela Cruz" required value={formData.fullNames} onChange={handleInputChange} error={errors.fullNames} />
            <Field icon={<Mail size={16} />} field="email" label="Email Address" type="email" placeholder="e.g. juan@company.com" required value={formData.email} onChange={handleInputChange} error={errors.email} />
            <Field icon={<Phone size={16} />} field="phone" label="Phone / Contact No." placeholder="e.g. 0917-123-4567" value={formData.phone} onChange={handleInputChange} error={errors.phone} />
            <Field icon={<Building2 size={16} />} field="address" label="Address" placeholder="e.g. Makati City" value={formData.address} onChange={handleInputChange} error={errors.address} />
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <label>About</label>
            <textarea
              value={formData.about}
              onChange={(e) => handleInputChange('about', e.target.value)}
              placeholder="Brief description about the user..."
              rows={3}
              className="input-field"
              style={{ resize: 'vertical', paddingLeft: '0.875rem' }}
            />
          </div>
        </div>

        {/* Account Info */}
        <div className="form-section">
          <div className="section-title"><Hash size={18} color="#3b82f6" />Account Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }}>
            <Field icon={<User size={16} />} field="username" label="Username" placeholder="e.g. juan_delacruz" required value={formData.username} onChange={handleInputChange} error={errors.username} />

            {/* Password with show/hide */}
            <div>
              <label>Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none', zIndex: 1 }}>
                  <Hash size={16} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter password"
                  className={`input-field ${errors.password ? 'input-error' : ''}`}
                  style={{ paddingLeft: '2.75rem', paddingRight: '3rem' }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.25rem' }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.3rem' }}>{errors.password}</p>}
            </div>

            {/* Role dropdown */}
            <div>
              <label>Role</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none', zIndex: 1 }}>
                  <Briefcase size={16} />
                </div>
                <select value={formData.role} onChange={(e) => handleInputChange('role', e.target.value)} className="select-field">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>
            </div>

            {/* Language dropdown */}
            <div>
              <label>Language</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none', zIndex: 1 }}>
                  <Hash size={16} />
                </div>
                <select value={formData.language} onChange={(e) => handleInputChange('language', e.target.value)} className="select-field">
                  <option value="en">English</option>
                  <option value="fil">Filipino</option>
                  <option value="ja">Japanese</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
            </div>

            {/* Active Status Toggle */}
            <div>
              <label>Active Status</label>
              <div style={{ padding: '0.75rem 1rem', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '45px', boxSizing: 'border-box' }}>
                <span style={{ color: formData.isActive === 1 ? '#34d399' : '#64748b', fontSize: '0.9rem', fontWeight: '500' }}>
                  {formData.isActive === 1 ? 'Active' : 'Inactive'}
                </span>
                <button type="button" onClick={() => handleInputChange('isActive', formData.isActive === 1 ? 0 : 1)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem', borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                  {formData.isActive === 1
                    ? <ToggleRight size={32} color="#10b981" />
                    : <ToggleLeft size={32} color="#64748b" />
                  }
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={resetForm} className="btn btn-outline">Reset Form</button>
          <button onClick={handleSubmit} disabled={loading} className="btn btn-success">
            {loading ? <><div className="spinner-sm"></div>Saving...</> : <><Save size={18} />Save Customer</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddCustomerList;